/**
 * ProductCatalogue — admin catalogue and landing-page packages.
 * Route: /products
 */
import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  AppBadge, AppButton, AppCard, AppField, AppFormAlert, AppInput, AppModal, AppSelect, ConfirmAction,
} from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { api } from "../../../shared/api/client";
import { getUser, hasPermission } from "../../auth/session";

type Product = {
  id: number;
  name: string;
  sku: string | null;
  price: number;
  currency: string;
  is_active: boolean;
  is_public?: boolean;
  daily_price?: number | null;
  tagline?: string | null;
  features?: string[];
  badge?: string | null;
  landing_sort?: number;
  requires_openpaygo: boolean;
  default_payment_period_months: number;
};

type Category = { id: number; name: string; level: number; path: string };

type FormState = {
  name: string;
  sku: string;
  price: string;
  currency: string;
  category_id: string;
  default_payment_period_months: string;
  requires_openpaygo: string;
  is_public: string;
  daily_price: string;
  tagline: string;
  features: string;
  badge: string;
  landing_sort: string;
  is_active: string;
};

const emptyForm = (): FormState => ({
  name: "",
  sku: "",
  price: "",
  currency: "TZS",
  category_id: "",
  default_payment_period_months: "12",
  requires_openpaygo: "false",
  is_public: "false",
  daily_price: "",
  tagline: "",
  features: "",
  badge: "",
  landing_sort: "100",
  is_active: "true",
});

function productToForm(p: Product): FormState {
  return {
    name: p.name,
    sku: p.sku || "",
    price: String(p.price ?? ""),
    currency: p.currency || "TZS",
    category_id: "",
    default_payment_period_months: String(p.default_payment_period_months ?? 12),
    requires_openpaygo: p.requires_openpaygo ? "true" : "false",
    is_public: p.is_public ? "true" : "false",
    daily_price: p.daily_price != null ? String(p.daily_price) : "",
    tagline: p.tagline || "",
    features: (p.features || []).join("\n"),
    badge: p.badge || "",
    landing_sort: String(p.landing_sort ?? 100),
    is_active: p.is_active ? "true" : "false",
  };
}

function formPayload(form: FormState) {
  return {
    name: form.name,
    price: parseFloat(form.price),
    currency: form.currency,
    sku: form.sku || undefined,
    category_id: form.category_id ? parseInt(form.category_id, 10) : undefined,
    default_payment_period_months: parseInt(form.default_payment_period_months, 10),
    requires_openpaygo: form.requires_openpaygo === "true",
    is_public: form.is_public === "true",
    daily_price: form.daily_price ? parseFloat(form.daily_price) : null,
    tagline: form.tagline || null,
    features: form.features
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean),
    badge: form.badge || null,
    landing_sort: parseInt(form.landing_sort, 10) || 100,
    is_active: form.is_active === "true",
  };
}

export function ProductCatalogue() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [fetchStatus, setFetchStatus] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [confirmDeactivate, setConfirmDeactivate] = useState<Product | null>(null);
  const [deactivateError, setDeactivateError] = useState("");
  const user = getUser();
  const canCreate = hasPermission(user, "product.create");
  const canUpdate = hasPermission(user, "product.update");
  const canDelete = hasPermission(user, "product.delete");

  const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setFetchStatus("loading");
    try {
      const [pd, cd] = await Promise.all([
        api.get<{ products: Product[] }>("/products?active=false"),
        api.get<{ categories: Category[] }>("/products/categories"),
      ]);
      setProducts(pd.products ?? []);
      setCategories(cd.categories ?? []);
      setFetchStatus("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load products");
      setFetchStatus("error");
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    if (!canCreate) return;
    setEditing(null);
    setForm(emptyForm());
    setFormErr("");
    setModalMode("create");
  };

  const openEdit = (p: Product) => {
    if (!canUpdate) return;
    setEditing(p);
    setForm(productToForm(p));
    setFormErr("");
    setModalMode("edit");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.price) {
      setFormErr("Name and price are required");
      return;
    }
    setBusy(true); setFormErr("");
    try {
      const body = formPayload(form);
      if (modalMode === "create") {
        await api.post("/products", body);
      } else if (editing) {
        await api.put(`/products/${editing.id}`, body);
      }
      setModalMode(null);
      await load();
    } catch (ex: unknown) {
      setFormErr(ex instanceof Error ? ex.message : "Failed");
    } finally { setBusy(false); }
  };

  const deactivate = async (p: Product) => {
    if (!canDelete) return;
    setBusy(true);
    setDeactivateError("");
    try {
      await api.delete(`/products/${p.id}`);
      setConfirmDeactivate(null);
      await load();
    } catch (ex: unknown) {
      setDeactivateError(ex instanceof Error ? ex.message : "Failed to deactivate product");
    } finally {
      setBusy(false);
    }
  };

  const columns: ColumnDef<Product, unknown>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "sku", header: "SKU" },
    {
      id: "daily",
      header: "Daily price",
      accessorFn: (r) =>
        r.daily_price != null
          ? `${r.currency} ${Number(r.daily_price).toLocaleString()}/day`
          : "—",
    },
    {
      id: "price",
      header: "List price",
      accessorFn: (r) => `${r.currency} ${Number(r.price).toLocaleString()}`,
    },
    {
      id: "public",
      header: "Landing",
      cell: ({ row }) =>
        row.original.is_public
          ? <AppBadge tone="success">Public</AppBadge>
          : <AppBadge tone="neutral">Hidden</AppBadge>,
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) =>
        row.original.is_active
          ? <AppBadge tone="success"><IconCheck size={12} /> Active</AppBadge>
          : <AppBadge tone="neutral">Inactive</AppBadge>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="pg-row-actions">
          {canUpdate && (
            <AppButton type="button" variant="secondary" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
              Edit
            </AppButton>
          )}
          {canDelete && row.original.is_active && (
            <AppButton type="button" variant="secondary" disabled={busy} onClick={(e) => { e.stopPropagation(); setConfirmDeactivate(row.original); }}>
              Deactivate
            </AppButton>
          )}
        </div>
      ),
    },
  ];

  const listStatus =
    fetchStatus === "loading" ? "loading"
    : fetchStatus === "error" ? "error"
    : products.length === 0 ? "empty"
    : "success";

  return (
    <>
      <AppFormAlert
        open={!!formErr}
        title={modalMode === "edit" ? "Product could not be updated" : "Product could not be created"}
        message={formErr}
        onClose={() => setFormErr("")}
      />
      {modalMode && (
        <AppModal
          open={!!modalMode}
          onClose={() => setModalMode(null)}
          title={modalMode === "create" ? "Create product" : `Edit: ${editing?.name}`}
        >
          <form onSubmit={submit}>
            <AppField label="Name">
              <AppInput value={form.name} onChange={(e) => set("name", e.target.value)} disabled={busy} autoFocus />
            </AppField>
            <div className="pg-two-col-form">
              <AppField label="SKU">
                <AppInput value={form.sku} onChange={(e) => set("sku", e.target.value)} disabled={busy} />
              </AppField>
              <AppField label="Currency">
                <AppInput value={form.currency} onChange={(e) => set("currency", e.target.value)} disabled={busy} />
              </AppField>
            </div>
            <div className="pg-two-col-form">
              <AppField label="List price">
                <AppInput type="number" value={form.price} onChange={(e) => set("price", e.target.value)} disabled={busy} />
              </AppField>
              <AppField label="Daily price (landing)">
                <AppInput type="number" value={form.daily_price} onChange={(e) => set("daily_price", e.target.value)} disabled={busy} />
              </AppField>
            </div>
            <AppField label="Tagline">
              <AppInput value={form.tagline} onChange={(e) => set("tagline", e.target.value)} disabled={busy} />
            </AppField>
            <AppField label="Features (one per line)">
              <textarea
                className="pg-input"
                rows={4}
                value={form.features}
                onChange={(e) => set("features", e.target.value)}
                disabled={busy}
                style={{ width: "100%" }}
              />
            </AppField>
            <div className="pg-two-col-form">
              <AppField label="Badge (e.g. Most popular)">
                <AppInput value={form.badge} onChange={(e) => set("badge", e.target.value)} disabled={busy} />
              </AppField>
              <AppField label="Landing sort">
                <AppInput type="number" value={form.landing_sort} onChange={(e) => set("landing_sort", e.target.value)} disabled={busy} />
              </AppField>
            </div>
            <div className="pg-two-col-form">
              <AppField label="Show on website">
                <AppSelect value={form.is_public} onChange={(e) => set("is_public", e.target.value)} disabled={busy}>
                  <option value="true">Yes — public</option>
                  <option value="false">No</option>
                </AppSelect>
              </AppField>
              <AppField label="Active">
                <AppSelect value={form.is_active} onChange={(e) => set("is_active", e.target.value)} disabled={busy}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </AppSelect>
              </AppField>
            </div>
            <AppField label="Category">
              <AppSelect value={form.category_id} onChange={(e) => set("category_id", e.target.value)} disabled={busy}>
                <option value="">None</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </AppSelect>
            </AppField>
            <AppField label="OpenPAYGO required">
              <AppSelect value={form.requires_openpaygo} onChange={(e) => set("requires_openpaygo", e.target.value)} disabled={busy}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </AppSelect>
            </AppField>
            <div className="pg-modal-actions">
              <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14} /> Saving…</> : "Save"}</AppButton>
              <AppButton type="button" variant="secondary" onClick={() => setModalMode(null)} disabled={busy}>Cancel</AppButton>
            </div>
          </form>
        </AppModal>
      )}

      <PageHeader
        title="Product catalogue"
        subtitle="Manage products shown on the public website."
        actions={canCreate ? <AppButton type="button" onClick={openCreate}>Add product</AppButton> : undefined}
      />
      {deactivateError && (
        <div className="pg-inline-error" role="alert">{deactivateError}</div>
      )}

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No products yet"
        emptyHint="Add a product to get started."
      >
        <AppCard>
          <AppDataTable data={products} columns={columns} />
        </AppCard>
      </PageState>

      {canDelete && (
        <ConfirmAction
          open={!!confirmDeactivate}
          title="Deactivate product?"
          message={
            confirmDeactivate
              ? `${confirmDeactivate.name} will be hidden from catalogues (soft deactivate — not permanently deleted).`
              : ""
          }
          confirmLabel="Deactivate"
          danger
          busy={busy}
          onCancel={() => setConfirmDeactivate(null)}
          onConfirm={() => {
            if (confirmDeactivate) void deactivate(confirmDeactivate);
          }}
        />
      )}
    </>
  );
}
